import { useParams } from "react-router-dom";
import Breadcrumb from "../../common/breadcrumb/breadcrumb";
import { Site } from "../../../types/Site";
import { useEffect, useState } from "react";
import SiteService from "../../../services/site.service";
import { useTranslation } from "react-i18next";
import SimpleMap from "../../common/SimpleMap";
import { toastSuccess } from "../../../App";
import DeviceTable from "../../fleet/DeviceTable";
import DeviceService from "../../../services/device.service";
import { Device } from "../../../types/Device";

const COORDINATE_POLL = 120 * 1000;

export default function SitePage() {
    const { t } = useTranslation();
    const { siteId } = useParams();
    const [site, setSite] = useState<Site>();
    const [devices, setDevices] = useState<Device[]>();
    const [address1, setAddress1] = useState("");
    const [locality, setLocality] = useState("");
    const [region, setRegion] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [isMobile, setIsMobile] = useState(false);
    const [country, setCountry] = useState("");

    const breadcrumbPages = [
        { name: 'Site List', href: '/sites', current: false, isHome: true },
        { name: t('sites.siteDetail'), href: `/sites/${siteId}`, current: false },
    ];

    const canSave = address1.trim() !== '' && locality.trim() !== '' && region.trim() !== '' && postalCode.trim() !== '' && country.trim() !== '';

    const handleSave = () => {
        if (siteId) {
            const updatedSite: any = {
                isMobile,
                address: {
                    ...(site?.address?.address1 !== address1 && { address1 }),
                    ...(site?.address?.locality !== locality && { locality }),
                    ...(site?.address?.region !== region && { region }),
                    ...(site?.address?.postalCode !== postalCode && { postalCode }),
                    ...(site?.address?.country !== country && { country })
                }
            };

            SiteService.updateSite(siteId, updatedSite).then(response => {
                response.json().then(data => {
                    setSite(data);
                    toastSuccess(t('sites.siteUpdated'));
                });
            });
        }
    };

    const fetchDevices = async () => {
        if (siteId) {
            const deviceResponse = await DeviceService.getDevices(siteId);
            const deviceData = await deviceResponse.json();
            setDevices(deviceData);
        }
    };

    const fetchData = async () => {
        if (siteId) {
            const siteResponse = await SiteService.getSite(siteId);
            const siteData = await siteResponse.json();

            setSite(siteData);
            setAddress1(siteData.address.address1);
            setLocality(siteData.address.locality);
            setRegion(siteData.address.region);
            setPostalCode(siteData.address.postalCode);
            setCountry(siteData.address.country);
            setIsMobile(siteData.isMobile);
        }
    };

    useEffect(() => {
      if (isMobile === true) {
        setInterval(() => {
          fetchData();
        }, COORDINATE_POLL);
      } else {
        fetchDevices();
        fetchData();
      }
    }, []);

    return (
        <>
            <div className="pt-3 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <Breadcrumb pages={breadcrumbPages}></Breadcrumb>

                {/* Flex container to align map on the left and form on the right */}
                <div className="flex flex-row items-start justify-between pt-3">
                    {/* Map container positioned to the left */}
                    <div className="mr-6 w-64 h-64 flex-shrink-0 z-0">
                        <SimpleMap coordinates={site?.location?.coordinates} className="rounded-md shadow-md" />
                    </div>

                    {/* Address form section */}
                    <div className="flex-grow">
                        <h2 className="text-lg font-bold leading-6 text-gray-900">{t('sites.site')}</h2>

                        <div className="mt-6 grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-4">
                                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                                    {t('sites.address')}
                                </label>
                                <input
                                    type="text"
                                    value={address1}
                                    onChange={e => setAddress1(e.target.value)}
                                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                                />
                            </div>

                            <div className="col-span-6 sm:col-span-2">
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700">{t('sites.city')}</label>
                                <input
                                    type="text"
                                    value={locality}
                                    onChange={e => setLocality(e.target.value)}
                                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                                />
                            </div>

                            <div className="col-span-6 sm:col-span-2">
                                <label htmlFor="state" className="block text-sm font-medium text-gray-700">{t('sites.state')}</label>
                                <input
                                    type="text"
                                    value={region}
                                    onChange={e => setRegion(e.target.value)}
                                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                                />
                            </div>

                            <div className="col-span-6 sm:col-span-2">
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700">{t('sites.country')}</label>
                                <input
                                    type="text"
                                    value={country}
                                    onChange={e => setCountry(e.target.value)}
                                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                                />
                            </div>

                            <div className="col-span-12 col-span-1">
                                <label htmlFor="zip" className="block text-sm font-medium text-gray-700">{t('sites.zip')}</label>
                                <input
                                    type="text"
                                    value={postalCode}
                                    onChange={e => setPostalCode(e.target.value)}
                                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                                />
                            </div>

                            <div className="col-span-12 col-span-1">
                                <label
                                    htmlFor="muted"
                                    className="block text-sm font-medium text-gray-700"
                                >
                                    {t('sites.mobileSite')}
                                </label>

                                <input
                                    type="checkbox"
                                    checked={isMobile}
                                    onChange={() => setIsMobile(!isMobile)}
                                    onClick={e => e.stopPropagation()}
                                    className="mt-1 w-5 h-5 rounded-md border border-gray-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end mt-6">
                    <button
                        type="button"
                        className={`rounded-md border border-transparent ${canSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-200'} py-2 px-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm`}
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        {t('save')}
                    </button>
                </div>

                {devices && <DeviceTable devices={devices} />}
            </div>
        </>
    );
}
